import React, { useState, useEffect } from "react";
import {
  Dialog,
  Typography,
  Box,
  Stack,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  FormControl,
  IconButton,
  Button,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";
import {
  getSelectDropdownUI,
  CustomDatePicker,
} from "../HelperFunctions/EmployeeFormHelpers";
import { getCorporations, getAccountTypes, getAccounts, saveAccount, clearAccountsCache } from "../api";

const AddCOA = ({
  open,
  onClose,
  onSuccess,
  corporationId = "",
  corporationName = "",
  // Formik context from parent
  setFieldValue,
  pendingCOAField,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [, setCorpName] = useState(corporationName);
  const [corpId, setCorpId] = useState(corporationId);
  const [corporations, setCorporations] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);
  const [accountType, setAccountType] = useState("");
  const [accountCode, setAccountCode] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [isSubaccount, setIsSubaccount] = useState(false);
  const [subaccount, setSubaccount] = useState("");
  const [openingBalance, setOpeningBalance] = useState("0.00");
  const [asOfDate, setAsOfDate] = useState(dayjs().format("MM-DD-YYYY"));
  const [subaccountsList, setSubaccountsList] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    setCorpName(corporationName);
    setCorpId(corporationId);

    if (open) {
      if (!corporationName) {
        getCorporations()
          .then((res) => {
            setCorporations(res || []);
          })
          .catch((err) => console.error("Failed to load corporations", err));
      }

      getAccountTypes()
        .then((res) => {
          setAccountTypes(res?.AccounTypes || res || []);
        })
        .catch((err) => console.error("Failed to load account types", err));
    }
  }, [open, corporationId, corporationName]);

  useEffect(() => {
    // Ensure that 0 is treated as a valid corpId, but exclude empty strings, null, or undefined
    const isValidCorpId =
      corpId !== "" && corpId !== null && corpId !== undefined;

    if (open && isValidCorpId && accountType) {
      getAccounts(corpId, accountType)
        .then((res) => {
          setSubaccountsList(res || []);
          setSubaccount(""); // Reset selection when list updates
        })
        .catch((err) => console.error("Failed to load subaccounts", err));
    } else {
      setSubaccountsList([]);
      setSubaccount("");
    }
  }, [open, corpId, accountType]);

  const handleBalanceChange = (e) => {
    const val = e.target.value;
    if (/^-?\d*\.?\d{0,2}$/.test(val)) {
      setOpeningBalance(val);
    } else if (val !== "" && val !== "-") {
      enqueueSnackbar("Please enter valid numbers", { variant: "error" });
    }
  };

  const handleBalanceBlur = () => {
    let val = parseFloat(openingBalance);
    if (isNaN(val) || String(openingBalance).trim() === '' || String(openingBalance).trim() === '-') {
      val = 0;
    }
    setOpeningBalance(val.toFixed(2));
  };

  const handleBalanceFocus = () => {
    if (Number(openingBalance) === 0) {
      setOpeningBalance("");
    }
  };

  const handleSave = async () => {
    if (!accountType || !accountNumber) return; // Prevent saving if required fields are empty

    setActionLoading(true);
    try {
      let finalAccountCode = accountCode.trim();

      // Check existing accounts for a dot-zero suffix (e.g. ".00000") to append automatically
      let zeroSuffix = "";
      subaccountsList.forEach((acc) => {
        let codeStr = acc.AccountCode || acc.Code || acc.accountCode || "";
        if (!codeStr) {
          const display = acc.displayLabel || acc.label || acc.Name || "";
          const match = String(display).match(/^([\d.]+)/);
          if (match) codeStr = match[1];
        }
        const suffixMatch = String(codeStr).match(/(\.0+)(?:\.|$)/);
        if (suffixMatch && suffixMatch[1].length > zeroSuffix.length) {
          zeroSuffix = suffixMatch[1];
        }
      });

      if (zeroSuffix && finalAccountCode && !finalAccountCode.includes('.')) {
        finalAccountCode += zeroSuffix;
      }

      const payload = {
        corporationID: corpId,
        accountTypeID: accountType,
        accountCode: finalAccountCode,
        accountName: accountNumber,
        parentAccId: isSubaccount && subaccount ? subaccount : "",
        openingBalance: parseFloat(openingBalance) || 0,
        asOf: dayjs(asOfDate).toISOString(),
      };

      const response = await saveAccount(payload);

      // If formik context is provided, handle the field update directly.
      if (response?.StatusCode === 200 || response?.statusCode === 200) {
        clearAccountsCache(corpId);
        if (setFieldValue && pendingCOAField) {
          const accounts = await getAccounts(corpId, "000000000000000000000000000000000000", true);

          const createdId = response?.AccID || response?.AccId || "";
          const combinedName = finalAccountCode && accountNumber ? `${finalAccountCode}. ${accountNumber}` : (accountNumber || finalAccountCode);
          const createdName = combinedName.trim().toLowerCase();

          const matchedCoa = accounts.find((item) => String(item.value || item.ID) === String(createdId)) ||
            accounts.find((item) => (item.displayLabel || item.label || item.Name || "").trim().toLowerCase() === createdName);

          const finalId = matchedCoa ? String(matchedCoa.value || matchedCoa.ID) : createdId;
          const finalName = matchedCoa ? (matchedCoa.displayLabel || matchedCoa.label || matchedCoa.Name) : combinedName;

          setFieldValue(pendingCOAField, finalId);
          const nameField = pendingCOAField.replace("Coa", "CoaName");
          setFieldValue(nameField, finalName);

          // Still call onSuccess for any other side-effects (like reloading dropdown options)
          if (onSuccess) {
            onSuccess();
          }
        } else if (onSuccess) {
          // Fallback for old consumers: just pass back the created object info
          const selectedType = accountTypes.find((t) => t.ID === accountType);
          const newId = response?.AccID || response?.AccId || "";
          const combinedName = finalAccountCode && accountNumber ? `${finalAccountCode}. ${accountNumber}` : (accountNumber || finalAccountCode);
          const newAccount = {
            label: combinedName,
            value: newId ? String(newId) : `NEW_COA_${Date.now()}`,
            name: combinedName,
            displayLabel: combinedName,
            type: selectedType ? selectedType.Name : "",
            accountTypeId: accountType,
          };
          onSuccess(newAccount);
        }
        handleClose();
      } else {
        enqueueSnackbar(response?.Status || "Failed to save account", { variant: "error" });
      }
    } catch (error) {
      console.error("Failed to save account:", error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleClose = () => {
    setCorpName(corporationName);
    setCorpId(corporationId);
    setAccountType("");
    setAccountCode("");
    setAccountNumber("");
    setIsSubaccount(false);
    setSubaccount("");
    setOpeningBalance("0.00");
    setAsOfDate(dayjs().format("MM-DD-YYYY"));
    onClose();
  };

  const inputSx = {
    width: 250,
    boxSizing: "border-box",
    "& .MuiOutlinedInput-root": {
      height: "25px",
      fontSize: "12px",
      backgroundColor: "white",
      boxSizing: "border-box",
    },
    "& .MuiOutlinedInput-input": {
      padding: "0 8px",
      fontSize: "12px",
      fontWeight: 500,
    },
  };

  const selectSx = {
    width: 250,
    boxSizing: "border-box",
    backgroundColor: "#fff",
    height: 25,
    minHeight: 25,
    borderRadius: "4px",
    border: "1px solid #c4c4c4",
    "&:hover:not(.Mui-disabled)": {
      backgroundColor: "#fff",
      border: "1px solid rgba(0, 0, 0, 0.87)",
    },
    "&.Mui-focused:not(.Mui-disabled)": {
      backgroundColor: "#fff",
      border: "1px solid #1976d2",
    },
    "&.Mui-disabled": {
      backgroundColor: "#E0E0E0 !important",
      border: "1px solid #c4c4c4 !important",
      borderRadius: "4px",
      cursor: "not-allowed",
      pointerEvents: "auto",
      "&:hover": {
        backgroundColor: "#E0E0E0 !important",
        border: "1px solid #c4c4c4 !important",
      },
      "& .MuiSelect-select": {
        WebkitTextFillColor: "#000 !important",
        color: "#000 !important",
        cursor: "not-allowed",
      },
      "& .MuiSelect-icon": {
        color: "#999 !important",
        cursor: "not-allowed",
      },
    },
    "& .MuiSelect-select": {
      padding: "0 10px",
      height: "25px",
      lineHeight: "25px",
      fontSize: "12px",
      display: "flex",
      alignItems: "center",
    },
  };

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      sx={{
        "& .MuiDialog-container": {
          alignItems: "flex-start",
        },
        "& .MuiDialog-paper": {
          height: { md: "55vh" },
          maxHeight: "100vh",
          width: { xs: "95vw", sm: "90vw", md: "74vw" },
          borderRadius: "0px",
          display: "flex",
          flexDirection: "column",
        },
      }}
      open={open}
      onClose={handleClose}
    >
      {/* Header matching CommonDialog */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 2,
          py: "6px",
          borderBottom: "1px solid #e0e0e0",
          backgroundColor: "#f8f9fa",
        }}
      >
        <Typography
          sx={{ fontSize: "16px", fontWeight: 500, color: "#2782EC" }}
        >
          Add New Account
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton
          onClick={handleClose}
          disableRipple
          sx={{
            width: 24,
            height: 24,
            borderRadius: "4px",
            backgroundColor: "#8c8c8c",
            color: "#fff",
            p: 0,
            "&:hover": { backgroundColor: "#7a7a7a" },
          }}
        >
          <Close sx={{ fontSize: "16px" }} />
        </IconButton>
      </Box>
      <Box
        sx={{ flexGrow: 1, overflowY: "auto", backgroundColor: "#fff", p: 4 }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography sx={{ width: 180, fontSize: "11px" }}>
            Corporation Name
          </Typography>
          {corporationName ? (
            <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>
              {corporationName}
            </Typography>
          ) : (
            <FormControl variant="filled" sx={{ width: 250 }}>
              <Select
                value={corpId}
                onChange={(e) => {
                  setCorpId(e.target.value);
                  const selected = corporations.find(
                    (c) => c.corpValue === e.target.value,
                  );
                  if (selected) setCorpName(selected.corpName);
                }}
                displayEmpty
                disableUnderline
                MenuProps={getSelectDropdownUI()}
                sx={selectSx}
              >
                <MenuItem value="" disabled>
                  --Select--
                </MenuItem>
                {corporations.map((c) => (
                  <MenuItem key={c.corpValue} value={c.corpValue}>
                    {c.corpName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography sx={{ width: 180, fontSize: "11px" }}>
            Account Type <span style={{ color: "red" }}>*</span>
          </Typography>
          <FormControl variant="filled" sx={{ width: 250 }}>
            <Select
              value={accountType}
              onChange={(e) => setAccountType(e.target.value)}
              displayEmpty
              disableUnderline
              MenuProps={getSelectDropdownUI()}
              sx={selectSx}
            >
              <MenuItem value="" disabled>
                --Select--
              </MenuItem>
              {accountTypes.map((type) => (
                <MenuItem key={type.ID} value={type.ID}>
                  {type.Name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography sx={{ width: 180, fontSize: "11px" }}>
            Account Code <span style={{ color: "red" }}>*</span>
          </Typography>
          <TextField
            size="small"
            value={accountCode}
            onChange={(e) => {
              if (/^[a-zA-Z0-9-.]*$/.test(e.target.value)) {
                setAccountCode(e.target.value);
              } else {
                enqueueSnackbar("Special characters are not allowed in Account Code.", { variant: "error" });
              }
            }}
            sx={inputSx}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography sx={{ width: 180, fontSize: "11px" }}>
            Account Name <span style={{ color: "red" }}>*</span>
          </Typography>
          <TextField
            size="small"
            value={accountNumber}
            onChange={(e) => {
              if (/^[a-zA-Z0-9\s]*$/.test(e.target.value)) {
                setAccountNumber(e.target.value);
              } else {
                enqueueSnackbar("Special characters are not allowed in Account Name.", { variant: "error" });
              }
            }}
            sx={inputSx}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography sx={{ width: 180, fontSize: "11px" }}>
            Subaccount
            <Checkbox
              checked={isSubaccount}
              onChange={(e) => {
                setIsSubaccount(e.target.checked);
                if (!e.target.checked) setSubaccount("");
              }}
              size="small"
              sx={{ p: 0.5 }}
            />
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ width: 250 }}
          >

            <FormControl variant="filled" sx={{ width: "100%", flex: 1 }}>
              <Select
                value={subaccount}
                disabled={!isSubaccount}
                onChange={(e) => setSubaccount(e.target.value)}
                disableUnderline
                displayEmpty
                MenuProps={getSelectDropdownUI()}
                sx={selectSx}
              >
                <MenuItem value="" disabled>
                  --Select--
                </MenuItem>
                {subaccountsList.map((acc) => (
                  <MenuItem key={acc.value || acc.ID} value={acc.value || acc.ID}>
                    {acc.displayLabel || acc.label || acc.Name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography sx={{ width: 180, fontSize: "11px" }}>
            Opening Balance
          </Typography>
          <TextField
            size="small"
            value={openingBalance}
            onChange={handleBalanceChange}
            onBlur={handleBalanceBlur}
            onFocus={handleBalanceFocus}
            inputProps={{ style: { textAlign: "right" } }}
            sx={inputSx}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", mb: 1, width: 430 }}>
          <CustomDatePicker
            heading="As of Date"
            name="asOfDate"
            value={asOfDate}
            setFieldValue={(name, value) => setAsOfDate(value)}
            labelWidth={180}
            width={250}
            spacing={0}
          />
        </Box>
        {/* Footer Buttons matching CommonDialog */}
        <Box sx={{ display: "flex", gap: 1, mt: 3 }}>
          <Button
            variant="contained"
            size="small"
            disabled={actionLoading}
            sx={{
              backgroundColor: "#b3e5fc",
              color: "#000",
              textTransform: "none",
              boxShadow: "none",
              borderRadius: "4px",
              minWidth: "100px",
              height: "30px",
              px: 1,
              fontSize: "11px",
              "&:hover": { backgroundColor: "#81d4fa", boxShadow: "none" },
            }}
            onClick={handleSave}
          >
            Save & Close
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={actionLoading}
            sx={{
              backgroundColor: "#EFDBE3",
              color: "#d32f2f",
              textTransform: "none",
              boxShadow: "none",
              borderRadius: "4px",
              minWidth: "100px",
              height: "30px",
              px: 1,
              fontSize: "11px",
              "&:hover": { backgroundColor: "#f48fb1", boxShadow: "none" },
            }}
            onClick={handleClose}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default AddCOA;
